import { Controller } from "@hotwired/stimulus";

// YouTube's ID is the named "id" group; Vimeo's is group 3; html5's extension is group 1.
const youtubePattern =
  /^https:\/\/(([^.]+\.)?youtube\.com\/(watch\?.*?v=|shorts\/)|youtu\.be\/)(?<id>[a-zA-Z0-9_-]{11}).*/;
const vimeoPattern = /^https:\/\/(([^.]+\.)?vimeo\.com\/)([0-9]+).*/;
// Allow http sources only on http pages (local dev/test); an https deployment
// requires https, since the browser would block http as mixed content anyway.
const html5Pattern =
  window.location.protocol === "https:"
    ? /^https:\/\/.*\.(mp4|webm|ogg)/i
    : /^https?:\/\/.*\.(mp4|webm|ogg)/i;

export default class extends Controller {
  static values = { videoUrl: String };

  videoUrlValueChanged() {
    const youtubeMatches = this.videoUrlValue.match(youtubePattern);
    const vimeoMatches = this.videoUrlValue.match(vimeoPattern);
    const html5Matches = this.videoUrlValue.match(html5Pattern);

    if (youtubeMatches) {
      const videoId = youtubeMatches.groups.id;
      this.embedVideo(this.buildYoutubeEmbed(videoId));
    } else if (vimeoMatches) {
      const videoId = vimeoMatches[3];
      this.embedVideo(this.buildVimeoEmbed(videoId));
    } else if (html5Matches) {
      const type = `video/${html5Matches[1].toLowerCase()}`;
      this.embedVideo(this.buildHtml5Embed(this.videoUrlValue, type));
    } else {
      console.warn(
        "URL provided for video embed does not appear to be a Youtube, Vimeo, or compatible video file URL.",
      );
    }
  }

  buildYoutubeEmbed(id) {
    return this.buildIframe(`https://www.youtube.com/embed/${id}`, {
      allow:
        "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share",
    });
  }

  buildVimeoEmbed(id) {
    return this.buildIframe(`https://player.vimeo.com/video/${id}`, {
      allow:
        "autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share",
    });
  }

  // Set src as a property (never parsed as markup) so the URL can't inject HTML.
  buildIframe(src, attributes) {
    const iframe = document.createElement("iframe");
    iframe.src = src;
    iframe.referrerPolicy = "strict-origin-when-cross-origin";
    iframe.allowFullscreen = true;
    iframe.sandbox =
      "allow-scripts allow-same-origin allow-presentation allow-fullscreen";
    Object.entries(attributes).forEach(([name, value]) =>
      iframe.setAttribute(name, value),
    );
    return iframe;
  }

  buildHtml5Embed(url, type) {
    const video = document.createElement("video");
    video.controls = true;
    const source = document.createElement("source");
    source.src = url;
    source.type = type;
    video.appendChild(source);
    return video;
  }

  embedVideo(element) {
    this.element.replaceChildren(element);
  }
}
